// Copyright 2019 Sorama B.V.

import {
    autoinject,
    bindable,
    bindingMode,
    computedFrom,
    customElement,
    InlineViewStrategy,
    RelativeViewStrategy,
    TaskQueue,
} from "aurelia-framework";
import { IGuidanceTopic } from "catalogs/GuidanceCatalog";
import { IWorkflowModuleCatalog } from "catalogs/IWorkflowModuleCatalog";
import { HelpWorkflowModuleCatalog } from "catalogs/WorkflowModuleCatalog/HelpWorkflowModuleCatalog";
import { IAuthentication } from "libraries/Authentication/IAuthentication";
import {
    IWorkflowModuleInstance,
    IWorkflowModuleLifetime,
} from "libraries/WorkflowFramework/Interfaces/IWorkflowModule";
import { WorkflowModuleType } from "libraries/WorkflowFramework/Interfaces/IWorkflowModuleConfig";
import { WorkflowPropertyBindingMode } from "libraries/WorkflowFramework/Interfaces/IWorkflowPropertyMetadata";
import { SidebarRouter } from "libraries/WorkflowFramework/Sidebar/SidebarRouter";
import { workflowProperty } from "libraries/WorkflowFramework/WorkflowProperties/WorkflowPropertyAttribute";
import { ShellViewManager } from "modules/shell/App/ShellViewManager";

@autoinject
@customElement("help-module")
export class HelpModule implements IWorkflowModuleLifetime {
    @bindable // NOTE: this is only allowed here, since we're also using this module as the guidance editor
    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public topicName: string;

    @workflowProperty({ bindingMode: WorkflowPropertyBindingMode.twoWay })
    public section: string;

    private guidanceModule: IGuidanceTopic;

    // Used for the Guidance Editor. Do NOT use these kind of constructions anywhere else!
    protected viewStrategy;

    @bindable({ defaultBindingMode: bindingMode.oneWay })
    public editMode = false;

    @bindable({ defaultBindingMode: bindingMode.oneWay })
    public template;

    constructor(
        private readonly moduleCatalog: IWorkflowModuleCatalog,
        private readonly htmlElement: Element,
        private readonly taskQueue: TaskQueue,
        private readonly shellviewManager: ShellViewManager,
        private readonly authentication: IAuthentication
    ) {
        this.topicNameChanged();
    }

    public activateModule(moduleInstance: IWorkflowModuleInstance): void | Promise<void> {
        // We don't want to leak sensitive information unauthenticated visitors of the portal
        if (!this.authentication.isSignedIn) {
            this.authentication.signIn();
        }

        this.topicNameChanged();
    }

    public deactivateModule(): void | Promise<void> {}

    protected topicNameChanged(): void {
        const topic = WorkflowModuleType.Help;
        const mods = <IGuidanceTopic[]>this.moduleCatalog.getWorkflowModules(topic);
        this.guidanceModule = mods.find((x) => x.name == this.topicName);

        this.render();

        this.taskQueue.queueTask(() => {
            this.sectionChanged();
        });
    }

    protected sectionChanged() {
        const elements = this.htmlElement.querySelectorAll("section");

        for (let i = 0; i < elements.length; i++) {
            elements[i].hidden = this.section ? this.section != elements[i].getAttribute("data-question-subid") : false;
        }
    }

    protected continueReading(): void {
        this.section = undefined;
    }

    protected changeTopic(topic: IGuidanceTopic): void {
        this.topicName = topic.name;
    }

    @computedFrom("guidanceModule")
    protected get breadcrumbItems(): IGuidanceTopic[] {
        if (!this.guidanceModule) return [];

        const result: IGuidanceTopic[] = [this.guidanceModule];
        let currentItem = this.guidanceModule;

        while (currentItem && currentItem.topicParent) {
            result.push(currentItem.topicParent);
            currentItem = currentItem.topicParent;
        }

        return result.reverse();
    }

    protected navigateToTopic(topic: IGuidanceTopic): void {
        this.shellviewManager.openRightSideBar(HelpWorkflowModuleCatalog.HelpModule, [
            {
                prefix: SidebarRouter.prefix,
                name: "topicName",
                value: topic.name,
            },
        ]);
    }

    private templateChanged() {
        this.render();
    }

    private render() {
        if (this.editMode) {
            this.viewStrategy = new InlineViewStrategy(
                `<template>
                    <require from="controls/guidance-link/GuidanceLink"></require>
                    ${this.template}
                </template>`
            );
        } else if (this.guidanceModule) {
            this.viewStrategy = new RelativeViewStrategy(this.guidanceModule.modulePath);
        }
    }
}
